import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Icon,
  useToast,
  Image,
  Grid,
  FormControl,
  FormLabel,
  Input,
  Link,
  
  useColorModeValue,
  Container,
  HStack,
  Divider,
  InputGroup,
  InputLeftElement,
  IconButton,
  Badge
} from '@chakra-ui/react';
import { WarningIcon, EmailIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { FaSignInAlt, FaGraduationCap, FaHome } from 'react-icons/fa';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import AuthContext from '../services/AuthContext';

const MotionBox = motion(Box);

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();
  const { login, isVerifying } = useContext(AuthContext);

  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = 'gray.50';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'خطأ في البريد الإلكتروني أو كلمة المرور';
      setError(errorMessage);
      toast({
        title: 'خطأ في الدخول',
        description: errorMessage,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={bgColor}>
        <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor} dir="rtl">
      <Header />

      <Container maxW="1200px" py={{ base: 10, md: 20 }}>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={16} alignItems="center">
          {/* Left Side: Visual/Context */}
          <VStack align="flex-start" spacing={10} display={{ base: 'none', lg: 'flex' }}>
            <VStack align="flex-start" spacing={6}>
              <Badge bg={primaryColor} color={secondaryColor} px={4} py={1} borderRadius="full" fontSize="sm" fontWeight="800" fontFamily="Tajawal, sans-serif">
                فضاء التلميذ
              </Badge>
              <Heading size="2xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor} lineHeight="1.3">
                مرحباً بك مجدداً في <br />
                <Text as="span" color={primaryColor}>عائلة المتميزين</Text>
              </Heading>
              <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                قم بتسجيل الدخول للوصول إلى دروسك، متابعة حصصك المباشرة، وحل التمارين التفاعلية. رحلة نجاحك مستمرة معنا.
              </Text>
            </VStack>

            <Box position="relative" w="100%">
              <Box position="absolute" inset="-15px" bg={primaryColor} opacity="0.1" borderRadius="3xl" transform="rotate(-2deg)" />
              <Image
                src="https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=800"
                alt="دخول التلاميذ"
                borderRadius="2xl"
                boxShadow="2xl"
                fallbackSrc="https://ui-avatars.com/api/?name=Cobra+Education&background=F2C919&color=2C3E50&size=600"
              />
              <Box position="absolute" bottom="20px" right="20px" bg="white" p={4} borderRadius="xl" boxShadow="xl" borderRight="4px solid" borderColor={primaryColor}>
                <HStack spacing={3}>
                  <Icon as={FaGraduationCap} color={primaryColor} boxSize={6} />
                  <Text fontSize="sm" fontFamily="Tajawal, sans-serif" fontWeight="800" color={secondaryColor}>
                    أكثر من 1200 تلميذ متفوق
                  </Text>
                </HStack>
              </Box>
            </Box>
          </VStack>

          {/* Right Side: Login Form */}
          <MotionBox
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Box
              bg="white"
              p={{ base: 8, md: 12 }}
              borderRadius="3xl"
              boxShadow="2xl"
              border="2px solid"
              borderColor="gray.100"
              maxW="500px"
              mx="auto"
            >
              <VStack spacing={8} align="stretch">
                <VStack spacing={4} align="center">
                  <Image
                    src="https://i.imgur.com/EFLnpkN.png"
                    alt="كوبرا للتعليم"
                    h="65px"
                    objectFit="contain"
                  />
                  <VStack spacing={1}>
                    <Heading size="lg" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                      دخول التلاميذ
                    </Heading>
                    <Text fontSize="md" fontFamily="Tajawal, sans-serif" color="gray.500" fontWeight="600">
                      ادخل بياناتك لمتابعة دروسك
                    </Text>
                  </VStack>
                </VStack>

                {error && (
                  <Box bg="red.50" p={4} borderRadius="xl" border="1px solid" borderColor="red.100">
                    <HStack>
                      <Icon as={WarningIcon} color="red.500" />
                      <Text color="red.700" fontSize="sm" fontFamily="Tajawal, sans-serif" fontWeight="700">
                        {error}
                      </Text>
                    </HStack>
                  </Box>
                )}

                <form onSubmit={handleSubmit}>
                  <VStack spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">البريد الإلكتروني</FormLabel>
                      <InputGroup>
                        <InputLeftElement h="55px" pointerEvents="none">
                          <EmailIcon color="gray.600" />
                        </InputLeftElement>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="example@mail.com"
                          bg="gray.50"
                          h="55px"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="transparent"
                          _focus={{ borderColor: primaryColor, bg: 'white' }}
                          fontFamily="Tajawal, sans-serif"
                        />
                      </InputGroup>
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700">كلمة المرور</FormLabel>
                      <InputGroup>
                        <InputLeftElement h="55px" pointerEvents="none">
                          <LockIcon color="gray.600" />
                        </InputLeftElement>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="كلمة السر الخاصة بك"
                          bg="gray.50"
                          h="55px"
                          borderRadius="xl"
                          border="2px solid"
                          borderColor="transparent"
                          _focus={{ borderColor: primaryColor, bg: 'white' }}
                          fontFamily="Tajawal, sans-serif"
                        />
                        <IconButton
                          aria-label="إظهار كلمة المرور"
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          onClick={() => setShowPassword(!showPassword)}
                          variant="ghost"
                          h="55px"
                          _hover={{ color: primaryColor }}
                        />
                      </InputGroup>
                    </FormControl>

                    <Flex justify="flex-start" w="100%">
                      <Link
                        as={RouterLink}
                        to="/forgot-password"
                        color={primaryColor}
                        fontSize="sm"
                        fontFamily="Tajawal, sans-serif"
                        fontWeight="700"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        نسيت كلمة المرور؟
                      </Link>
                    </Flex>

                    <Button
                      type="submit"
                      bg={primaryColor}
                      color={secondaryColor}
                      w="100%"
                      h="60px"
                      fontSize="xl"
                      fontWeight="900"
                      fontFamily="Tajawal, sans-serif"
                      borderRadius="xl"
                      boxShadow="lg"
                      isLoading={isLoading}
                      _hover={{ bg: '#d4b016', transform: 'translateY(-2px)' }}
                      _active={{ transform: 'translateY(0)' }}
                      leftIcon={<FaSignInAlt />}
                    >
                      تسجيل الدخول
                    </Button>
                  </VStack>
                </form>

                <VStack spacing={4}>
                  <Divider borderColor="gray.100" />
                  <Button
                    as={RouterLink}
                    to="/"
                    variant="ghost"
                    color="gray.500"
                    size="sm"
                    fontFamily="Tajawal, sans-serif"
                    fontWeight="700"
                    leftIcon={<FaHome />}
                    _hover={{ color: primaryColor, bg: 'transparent' }}
                  >
                    العودة للرئيسية
                  </Button>
                </VStack>
              </VStack>
            </Box>
          </MotionBox>
        </Grid>
      </Container>

      <Footer />
    </Box>
  );
};

export default LoginPage;
